import { MachineServices } from 'common/bento/types/MachineConfig';
import { apolloClient } from 'common/graphql/clients';
import webView from 'common/webView';
import { TrackSegmentEventDocument } from 'features/Analytics/graphql/mutations/trackSegmentEvent.gql';
import { OnboardingCompanyDocument } from 'features/Onboarding/graphql/queries/onboardingCompany.gql';

import { ApplicationType as StartApplicationType } from '../../../../../__generated__/GQL';
import { AbortApplicationDocument } from '../../../graphql/mutations/abortApplication.gql';
import { StartApplicationDocument } from '../../../graphql/mutations/startApplication.gql';
import { OnboardingUserDocument } from '../../../graphql/queries/onboardingUser.gql';
import { ApplicationType } from '../../../types';
import { SubmitStandardApplicationDocument } from '../graphql/mutations/submitStandardApplication.gql';
import { UpdateFinishedProfileSetupDocument } from '../graphql/mutations/updateFinishedProfileSetup.gql';
import { Model } from './model';

export enum Service {
  SubmitApplication = 'submitApplication',
  FinishProfileSetup = 'finishProfileSetup',
  AbortApplication = 'abortApplication',
  AbortToCapitalDeposit = 'abortToCapitalDeposit',
}

const services: MachineServices<Model, Service> = {
  abortApplication: async ({ getModuleData }) => {
    const { applicationTypeContext, companyContext } = getModuleData();

    if (!companyContext.companyProfileId) {
      throw new Error(`Can't call abortApplication without an application`);
    }

    const { companyProfileId } = companyContext;

    /**
     * We don't await this tracking event.
     */
    apolloClient.mutate({
      mutation: TrackSegmentEventDocument,
      variables: {
        input: {
          companyProfileId: companyProfileId,
          event: 'Onboarding Reset Requested',
        },
      },
    });

    await apolloClient.mutate({
      mutation: AbortApplicationDocument,
      variables: {
        input: {
          id: companyProfileId,
        },
      },
    });

    companyContext.clearCompanyContext();
    applicationTypeContext.dispatch(null);

    /**
     * To prevent any risk of stale data, we clear the apollo cache and refetch all active
     * queries after a reset. This is more or less equivalent to doing a browser refresh.
     */
    await apolloClient.resetStore();
  },

  abortToCapitalDeposit: async ({ getModuleData }) => {
    const { applicationTypeContext, companyContext } = getModuleData();

    if (!companyContext.companyProfileId) {
      throw new Error(`Can't call abortApplication without an application`);
    }

    const { companyProfileId } = companyContext;

    await apolloClient.mutate({
      mutation: AbortApplicationDocument,
      variables: {
        input: {
          id: companyProfileId,
        },
      },
    });

    const { data } = await apolloClient.mutate({
      mutation: StartApplicationDocument,
      refetchQueries: [OnboardingUserDocument],
      variables: {
        input: {
          type: StartApplicationType.CapitalDeposit,
        },
      },
    });

    if (!data) {
      throw new Error('No data returned from startApplication mutation');
    }

    applicationTypeContext.dispatch(ApplicationType.CapitalDeposit);
    getModuleData().companyContext.setCompanyContext({
      companyProfileId: data.startApplication.companyProfileId,
      companyUserId: undefined,
    });
  },

  finishProfileSetup: async ({ getModuleData }) => {
    const { companyData } = getModuleData();

    if (!companyData) {
      throw new Error(`Can't call submitApplication without a company`);
    }

    const { company } = companyData;

    /**
     * We don't await this tracking event.
     */
    apolloClient.mutate({
      mutation: TrackSegmentEventDocument,
      variables: {
        input: {
          companyProfileId: company.companyProfileId,
          event: 'Applicant Profile Completed',
          properties: {
            accountType: 'standard',
            isMicro: company.profile.isMicro,
            isMultiKYC: company.isMultiKYC,
            legalForm: company.profile.legalForm,
            withWebView: webView.isInWebView,
          },
        },
      },
    });

    return apolloClient.mutate({
      mutation: UpdateFinishedProfileSetupDocument,
      refetchQueries: [
        {
          query: OnboardingCompanyDocument,
          variables: { companyProfileId: company.companyProfileId },
        },
      ],
      variables: {
        metadata: {
          hasFinishedProfileSetup: true,
        },
      },
    });
  },

  submitApplication: async ({ getModuleData }) => {
    const { companyData } = getModuleData();

    if (!companyData) {
      throw new Error(`Can't call submitApplication without a company`);
    }

    try {
      await apolloClient.mutate({
        awaitRefetchQueries: true,
        mutation: SubmitStandardApplicationDocument,
        refetchQueries: [
          {
            query: OnboardingCompanyDocument,
            variables: {
              companyProfileId: companyData.company.companyProfileId,
            },
          },
        ],
        variables: {
          input: {
            companyUserId: companyData.companyUser.companyUserId,
          },
        },
      });
    } catch (error) {
      await apolloClient.refetchQueries({
        include: [OnboardingCompanyDocument],
      });
      throw error;
    }
  },
};

export default services;
